<template>
  <PanelLayout
    id="service-panel"
    :loading="serviceLoading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="onCancel"
    :unsavedChanges="unsavedChanges"
    @cancel="onCancel"
    @save="onSave"
  >
    <template #actions-right>
      <div v-if="serviceData && !serviceData.isNewRecord" class="flex items-center space-x-2">
        <o-tooltip :text="$t('general.delete')">
          <o-button icon="delete" flat @click="handleDelete" />
        </o-tooltip>

        <OHistoryLink
          :item="serviceData"
          model="Service"
          icon="history"
          slim
          flat
        />
      </div>
    </template>

    <template #heading>
      <div v-if="serviceData" class="flex items-center space-x-2">
        <div>
          <h2
            class="text-lg md:text-xl tracking-tight font-bold leading-none/85"
          >
            {{ serviceData.isNewRecord ? $t('service.new') : serviceData.title }}
          </h2>
          <div v-if="!serviceData.isNewRecord" class="mt-1">
            <p class="opacity-80 text-sm md:text-base" v-html="$t('pages.service.edit.id', { id: serviceData.idService })" />
          </div>
        </div>
      </div>
    </template>

    <div v-if="serviceData" class="h-full w-full">
      <OTabs ref="tabsRef" nav-class="bg-gray-50 h-11 border-b px-2 dark:bg-gray-900">
        <OTab :name="$t('general.details')" icon="details" scroll>
          <ServiceDetails
            ref="detailsRef"
            v-model:service="serviceData"
            @selectTab="handleSelectTab"
            @updatePrices="handleUpdatePrices"
            @updateServiceCategory="handleUpdateServiceCategory"
            @updateImageSuccess="onUpdate"
          />
        </OTab>
        <OTab :name="$t('general.onlineBooking')" icon="menuOnlineBooking" scroll>
          <ServiceOnline v-model:service="serviceData" />
        </OTab>
        <OTab :name="$t('general.employees')" icon="employee">
          <ServiceEmployees
            v-model:service="serviceData"
            :handleEmployeePriceChange="onEmployeePriceChange"
          />
        </OTab>
        <OTab
          v-if="$features.get('resources') && !serviceData.isNewRecord"
          :name="$t('general.resources')"
          icon="resources"
          scroll
        >
          <ServiceResources v-model:service="serviceData" />
        </OTab>
        <OTab v-if="!serviceData.isNewRecord && $features.get('forms')" :name="$t('general.forms')" icon="forms" scroll>
          <ServiceForms v-model:service="serviceData" @update="onUpdate" />
        </OTab>
        <OTab v-if="!serviceData.isNewRecord && $features.get('service-automation')" :name="$t('service.automations')" icon="sparkles" scroll>
          <ServiceAutomations v-model:service="serviceData" @update="onUpdate" />
        </OTab>
        <OTab
          v-if="!serviceData.isNewRecord && $features.get('service-addons')"
          :is-visible="!serviceData.isServiceCustomerTypeGroup"
          :name="$t('service.addOns')"
          icon="charge"
          scroll
        >
          <ServiceAddOns :associations="associations" v-model:service="serviceData" @update="onUpdate" />
        </OTab>
        <OTab :name="$t('general.reminders')" icon="reminder" scroll>
          <ServiceReminders v-model:service="serviceData" />
        </OTab>
        <OTab :name="$t('pages.service.edit.advancedTiming')" icon="bufferTime" scroll>
          <ServiceTiming v-model:service="serviceData" />
        </OTab>
      </OTabs>
    </div>

    <template v-if="serviceData">
      <DeleteDialog ref="deleteDialogRef" @success="handleDeleteSuccess" />

      <UpdatePricesDialog
        ref="updatePricesDialogRef"
        :hasEmployeeServicePriceChanged="hasEmployeePriceChanged"
        @success="onUpdate"
      />
    </template>
  </PanelLayout>
</template>

<script setup lang="ts">
import ServiceDetails from '@/components/service/edit/Details.vue'
import ServiceEmployees from '@/components/service/edit/Employees.vue'
import ServiceReminders from '@/components/service/edit/Reminders.vue'
import ServiceTiming from '@/components/service/edit/Timing.vue'
import ServiceResources from '@/components/service/edit/Resources.vue'
import ServiceForms from '@/components/service/edit/Forms.vue'
import ServiceOnline from '@/components/service/edit/Online.vue'
import ServiceAutomations from '~/components/service/edit/Automations.vue'
import ServiceAddOns from '~/components/service/edit/AddOns.vue'

// @ts-ignore - No types available for this module
import DeleteDialog from '@/components/service/actions/Delete.vue'
// @ts-ignore - No types available for this module
import UpdatePricesDialog from '@/components/service/actions/UpdatePrices.vue'
// @ts-ignore - No types available for this module
import PanelLayout from '@/components/panel/PanelLayout.vue'

import { OTabs, OTab } from '@/components/elements'

import type { Service } from '~/_api/models/service'
import type { ServiceAssociation } from '~/_api/models/serviceAssociation'
import type { ServiceCategory } from '~/_api/models/serviceCategory'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{
  id?: string
  category?: string | number | null
}>()

// Use Nuxt app composables
const { $api, $notification, $bus, $panel, $router, $i18n, $dialog } = useNuxtApp()

// Template refs
const panel = usePanel()
const tabsRef = ref()
const detailsRef = ref()
const deleteDialogRef = ref()
const updatePricesDialogRef = ref()

// Additional state not handled by useEditModel
const associations = ref<ServiceAssociation[]>([])
const serviceCategory = ref<ServiceCategory | null>(null)
const hasEmployeePriceChanged = ref(false)

// Use the edit model composable
const {
  data: serviceData,
  loading: serviceLoading,
  saving: serviceSaving,
  changed: serviceChanged,
  onSubmit: onServiceSubmit,
  fetchData: fetchServiceData,
} = useEditModel<Service>({
  id: props.id || 'new',
  service: $api.service,
  successNotification: false,
  onAfterLoad: async (data: Service) => {
    // Set default category if creating new
    if (data.isNewRecord && props.category) {
      data.category = props.category as any
    }

    // Normalize employee allowInternet values
    for (const employee of data.employees || []) {
      if (employee.allowInternet === null) {
        employee.allowInternet = data.allowInternet
      }
    }

    // Load associations for existing services
    if (!data.isNewRecord) {
      const assocResponse = await $api.service.associations(data)
      if (assocResponse) {
        associations.value = assocResponse.items
      }
    }
  },
  onBeforeSubmit: async (data: Service) => {
    // Save new service category first if one was created
    if (serviceCategory.value) {
      const response = await $api.serviceCategory.save(serviceCategory.value)
      const savedCategory = response.data

      if (savedCategory) {
        data.category = savedCategory
        data.idServiceCategory = savedCategory.idServiceCategory
        detailsRef.value?.resetCreateCategory()
      }
    }
  },
  onAfterSubmit: async (response: any, data: Service) => {
    const savedService = response.data

    // Normalize employee allowInternet values
    for (const employee of savedService?.employees || []) {
      if (employee.allowInternet === null) {
        employee.allowInternet = savedService.allowInternet
      }
    }

    // Show update prices dialog if prices changed
    if (hasEmployeePriceChanged.value) {
      await updatePricesDialogRef.value?.open(data)
    }

    $notification.toast({
      title: data.isNewRecord ? 'Service created successfully' : 'Service updated successfully',
      text: 'Service information has been saved',
      variant: 'success',
    })

    $bus.emit.updated('Service', savedService)

    // If it was a new record, swap in-place to the freshly-created service's edit view.
    if (data.isNewRecord && savedService) {
      panel?.replace('service/edit', { id: String(savedService.idService) })
    }
  },
})

const unsavedChanges = computed<boolean>(() => serviceChanged.value)

const onCancel = (force: boolean = false): void => {
  if (!force && unsavedChanges.value) {
    if ($dialog) {
      $dialog.confirm($i18n.t('general.unsavedChangesInfo'), {
        title: $i18n.t('general.unsavedChanges'),
        confirm: {
          text: $i18n.t('general.saveAndClose'),
          callback: async () => {
            await onServiceSubmit()
            panel?.close(true)
            return true
          },
        },
        cancel: {
          text: $i18n.t('general.closeWithoutSaving'),
          callback: () => {
            panel?.close(true)
            return true
          },
        },
      })
      return
    }

    const shouldClose = typeof window === 'undefined'
      ? true
      : window.confirm(String($i18n.t('general.unsavedChangesInfo')))
    if (shouldClose) {
      panel?.close(true)
    }
    return
  }

  panel?.close(force)
}

const onSave = async (close: boolean = false): Promise<void> => {
  await onServiceSubmit()
  if (close) {
    panel?.close(true)
  }
}

// Event handlers
const onEmployeePriceChange = (): void => {
  hasEmployeePriceChanged.value = true
}

const onUpdate = (): void => {
  fetchServiceData()
  $bus.emit.updated('Service', serviceData.value)
}

const handleDelete = (): void => {
  deleteDialogRef.value?.open(serviceData.value)
}

const handleDeleteSuccess = (): void => {
  panel?.close()
}

const handleSelectTab = (tab: string): void => {
  tabsRef.value?.selectTab('#' + tab)
}

const handleUpdatePrices = async (): Promise<void> => {
  if (hasEmployeePriceChanged.value) {
    await onSave()
  } else {
    await updatePricesDialogRef.value?.open(serviceData.value)
  }
}

const handleUpdateServiceCategory = (category: ServiceCategory | null): void => {
  serviceCategory.value = category
}
</script>
